'use client';

import React, { useRef, useEffect, useState, useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, Compass, ChevronLeft, ChevronRight, Pause, Play, Gift, Utensils } from 'lucide-react';
import { useCommerce } from '@/context/CommerceContext';

interface WheelCard {
  id: string;
  name: string;
  subtitle: string;
  image: string;
  link: string;
  type: 'Snack Category' | 'Hamper Collection';
  typeKey: 'product' | 'hamper';
  angle: number; // degrees on the 360-deg wheel (0 is apex)
  x: number;     // px relative to 1600x1600 wheel center (800, 800)
  y: number;
}

interface RawCardItem {
  id: string;
  name: string;
  subtitle: string;
  image: string;
  link: string;
  type: 'Snack Category' | 'Hamper Collection';
  typeKey: 'product' | 'hamper';
}

// Wheel center (800, 800), Radius R = 648px for tight, harmonious fan spacing
const WHEEL_RADIUS = 648;
const WHEEL_CENTER = 800;

// Stationary radial dial ticks along the dome (radius 468px to 494px, center at X: 720, Y: 850 in a 1440px viewBox)
const DIAL_TICKS = Array.from({ length: 99 }, (_, i) => {
  const angle = -73.5 + i * 1.5;
  const rad = (angle * Math.PI) / 180;
  const isMajor = i % 4 === 0;
  const rInner = 468;
  const rOuter = isMajor ? 494 : 482;
  const x1 = Math.round((720 + rInner * Math.sin(rad)) * 10) / 10;
  const y1 = Math.round((850 - rInner * Math.cos(rad)) * 10) / 10;
  const x2 = Math.round((720 + rOuter * Math.sin(rad)) * 10) / 10;
  const y2 = Math.round((850 - rOuter * Math.cos(rad)) * 10) / 10;
  return { id: i, x1, y1, x2, y2, isMajor };
});

const STATS_DATA = [
  { value: '12+', label: 'Years of Experience' },
  { value: '25+', label: 'Artisanal Varieties' },
  { value: '98%', label: 'Customer Satisfaction' },
  { value: '100%', label: 'Cold-Pressed Oil' },
];

type CategoryFilter = 'all' | 'products' | 'hampers';

interface ShopByCategorySectionProps {
  onSelectCategory?: (categoryId: string) => void;
}

export default function ShopByCategorySection({ onSelectCategory }: ShopByCategorySectionProps) {
  const { categories: liveCategories, hamperCategories: liveHamperCategories, products } = useCommerce();

  const [filter, setFilter] = useState<CategoryFilter>('all');
  const [activeCard, setActiveCard] = useState<RawCardItem | null>(null);

  const wheelRef = useRef<HTMLDivElement>(null);
  const angleRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);

  // Drag state
  const isDraggingRef = useRef<boolean>(false);
  const startXRef = useRef<number>(0);

  // 1. Build unified Product Categories strictly from live database
  const productCategoryCards: RawCardItem[] = useMemo(() => {
    if (!liveCategories || liveCategories.length === 0) {
      return [];
    }

    return liveCategories
      .filter((c) => c.isActive !== false)
      .map((c) => {
        const slug = c.slug || c.id;
        const prodCount = products ? products.filter((p) => p.category === slug).length : 0;
        const subtitleText = prodCount > 0 ? `${prodCount} Fresh Varieties` : (c.description || 'Authentic South Indian snack');

        return {
          id: `product-cat-${slug}`,
          name: c.name || 'Snack Category',
          subtitle: subtitleText,
          image: c.image || '/images/pouch_kai_murukku.png',
          link: `/shop?category=${slug}`,
          type: 'Snack Category' as const,
          typeKey: 'product' as const,
        };
      });
  }, [liveCategories, products]);

  // 2. Build unified Hamper Categories from live database (strictly synchronized with database)
  const hamperCategoryCards: RawCardItem[] = useMemo(() => {
    if (!liveHamperCategories || liveHamperCategories.length === 0) {
      return [];
    }

    return liveHamperCategories
      .filter((h) => h.isActive !== false)
      .map((h) => {
        const value = h.value || h.id;
        const linkUrl =
          h.moduleType === 'festivals'
            ? '/festivals'
            : h.moduleType === 'corporate'
            ? '/corporate'
            : `/occasions?category=${value}`;

        return {
          id: `hamper-cat-${value}`,
          name: h.label || 'Gift Hamper',
          subtitle: h.description || h.defaultBadge || 'Traditional celebration hamper',
          image: h.image || '/images/occasion_wedding_toon.jpg',
          link: linkUrl,
          type: 'Hamper Collection' as const,
          typeKey: 'hamper' as const,
        };
      });
  }, [liveHamperCategories]);

  // 3. Assemble active list based on filter - normalized to 16 cards (22.5° step) for harmonious spacing
  const displayedCards: RawCardItem[] = useMemo(() => {
    const prods = productCategoryCards;
    const hampers = hamperCategoryCards;

    let base: RawCardItem[] = [];

    if (filter === 'products') {
      base = prods;
    } else if (filter === 'hampers') {
      base = hampers;
    } else {
      // 'all': If hampers exist, interleave; if no hampers, show all active products!
      if (hampers.length === 0) {
        base = prods;
      } else if (prods.length === 0) {
        base = hampers;
      } else {
        const maxLen = Math.max(prods.length, hampers.length);
        const combined: RawCardItem[] = [];
        for (let i = 0; i < maxLen; i++) {
          if (i < prods.length) combined.push(prods[i]);
          if (i < hampers.length) combined.push(hampers[i]);
        }
        base = combined;
      }
    }

    if (base.length === 0) return [];

    // Smoothly repeat active items to complete the 16-card 360-degree circular ring
    const targetCount = 16;
    const result: RawCardItem[] = [];
    for (let i = 0; i < targetCount; i++) {
      result.push(base[i % base.length]);
    }
    return result;
  }, [filter, productCategoryCards, hamperCategoryCards]);

  // 4. Calculate dynamic angle & coordinate layout on 1600x1600 stage (Angle step = 360 / 16 = 22.5°)
  const wheelCards: WheelCard[] = useMemo(() => {
    if (displayedCards.length === 0) return [];
    const total = displayedCards.length;
    const stepAngle = 360 / total;

    return displayedCards.map((card, i) => {
      const angle = i * stepAngle;
      const rad = (angle * Math.PI) / 180;
      const x = Math.round((WHEEL_CENTER + WHEEL_RADIUS * Math.sin(rad)) * 10) / 10;
      const y = Math.round((WHEEL_CENTER - WHEEL_RADIUS * Math.cos(rad)) * 10) / 10;
      return { ...card, angle, x, y };
    });
  }, [displayedCards]);

  // Set initial active card
  useEffect(() => {
    if (wheelCards.length > 0) {
      setActiveCard(wheelCards[0]);
    } else {
      setActiveCard(null);
    }
  }, [filter, wheelCards]);

  // Animation loop for continuous smooth circling rotation
  useEffect(() => {
    let animId: number;
    let lastTime = performance.now();

    const loop = (currentTime: number) => {
      const dt = currentTime - lastTime;
      lastTime = currentTime;

      if (!isPausedRef.current && wheelRef.current) {
        // Continuous smooth rotation (~3.5 degrees per second)
        angleRef.current = (angleRef.current + dt * 0.0035) % 360;
        wheelRef.current.style.transform = `translate(-50%, -50%) rotate(${angleRef.current}deg)`;
      }

      animId = requestAnimationFrame(loop);
    };

    animId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animId);
  }, []);

  const stepDegree = 22.5;

  const handleRotateBy = (deltaDeg: number) => {
    angleRef.current = (angleRef.current + deltaDeg) % 360;
    if (wheelRef.current) {
      wheelRef.current.style.transition = 'transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)';
      wheelRef.current.style.transform = `translate(-50%, -50%) rotate(${angleRef.current}deg)`;
      setTimeout(() => {
        if (wheelRef.current) wheelRef.current.style.transition = '';
      }, 650);
    }
  };

  const togglePlay = () => {
    isPausedRef.current = !isPausedRef.current;
    setIsPlaying(!isPausedRef.current);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    isDraggingRef.current = true;
    startXRef.current = e.clientX;
    isPausedRef.current = true;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDraggingRef.current || !wheelRef.current) return;
    const dx = e.clientX - startXRef.current;
    startXRef.current = e.clientX;
    angleRef.current = (angleRef.current + dx * 0.16) % 360;
    wheelRef.current.style.transform = `translate(-50%, -50%) rotate(${angleRef.current}deg)`;
  };

  const handlePointerUp = () => {
    isDraggingRef.current = false;
    if (isPlaying) {
      isPausedRef.current = false;
    }
  };

  return (
    <section id="categories" className="py-12 sm:py-16 lg:py-24 bg-white relative overflow-hidden select-none w-full border-b border-neutral-200/80">
      <div className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6">

        {/* 1. Stage Wrapper: Frozen overflow on mobile, clean horizontal scrollbar on large screens */}
        <div className="w-full overflow-hidden md:overflow-x-auto md:scrollbar-none pb-2 md:pb-4">
          <div
            className={`relative w-[1260px] md:w-[1260px] lg:w-[1380px] xl:w-[1440px] 2xl:w-[1500px] ${
              wheelCards.length === 0
                ? 'h-[480px] sm:h-[520px] md:h-[600px] lg:h-[640px]'
                : 'h-[500px] sm:h-[560px] md:h-[730px] lg:h-[780px]'
            } -ml-[240px] sm:-ml-[170px] md:mx-auto overflow-hidden cursor-grab active:cursor-grabbing select-none transition-all duration-300`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
          >
            {/* Stationary Precision SVG Dial & Radial Ticks (Desktop only) */}
            <svg
              className="hidden md:block absolute inset-0 w-full h-full pointer-events-none z-10"
              viewBox="0 0 1440 780"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Dotted Arch Guide Line */}
              <path
                d="M 272.4 713.2 A 468 468 0 0 1 1167.6 713.2"
                stroke="#D6C7B2"
                strokeWidth="1.2"
                strokeDasharray="4 4"
                strokeOpacity="0.8"
              />

              {/* Radial Ticks */}
              {DIAL_TICKS.map((t) => (
                <line
                  key={t.id}
                  x1={t.x1}
                  y1={t.y1}
                  x2={t.x2}
                  y2={t.y2}
                  stroke="#CBBCA8"
                  strokeWidth={t.isMajor ? 1.6 : 1.1}
                  strokeOpacity={t.isMajor ? 0.95 : 0.6}
                />
              ))}
            </svg>

            {/* THE REVOLVING 360-DEGREE WHEEL (Center at X: 50%, Y: 850px) */}
            <div
              ref={wheelRef}
              style={{
                left: '50%',
                top: '850px',
                transform: 'translate(-50%, -50%) rotate(0deg)',
              }}
              className="absolute w-[1600px] h-[1600px] rounded-full will-change-transform pointer-events-auto"
            >
              {wheelCards.map((cat, idx) => (
                <div
                  key={`${cat.id}-${idx}`}
                  style={{
                    left: `${cat.x}px`,
                    top: `${cat.y}px`,
                    transform: `translate(-50%, -50%) rotate(${cat.angle}deg)`,
                  }}
                  className="absolute z-20 transition-all duration-300 hover:scale-105 hover:z-30 group"
                  onMouseEnter={() => setActiveCard(cat)}
                >
                  <Link
                    href={cat.link}
                    onClick={() => onSelectCategory?.(cat.id)}
                    className="block relative w-[184px] h-[224px] sm:w-[196px] sm:h-[238px] md:w-[208px] md:h-[250px] lg:w-[214px] lg:h-[256px] rounded-3xl overflow-hidden shadow-lg group-hover:shadow-2xl border-[3px] border-white bg-white transition-all duration-300"
                  >
                    <Image
                      src={cat.image || '/images/cat_traditional_savouries.png'}
                      alt={cat.name || 'Category'}
                      fill
                      sizes="(max-width: 640px) 190px, 225px"
                      unoptimized
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    {/* Category Type Badge (Snack vs Hamper) */}
                    <div className="absolute top-2.5 left-2.5 z-20">
                      <span
                        className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase shadow-xs backdrop-blur-md transition-colors ${
                          cat.typeKey === 'product'
                            ? 'bg-[#0F382A]/90 text-[#F2BC6F] border border-[#F2BC6F]/40'
                            : 'bg-[#6F0A06]/90 text-amber-100 border border-amber-300/40'
                        }`}
                      >
                        {cat.typeKey === 'product' ? (
                          <>
                            <Utensils className="w-2.5 h-2.5 mr-0.5 text-[#F2BC6F]" />
                            <span>Snacks</span>
                          </>
                        ) : (
                          <>
                            <Gift className="w-2.5 h-2.5 mr-0.5 text-amber-200" />
                            <span>Hampers</span>
                          </>
                        )}
                      </span>
                    </div>

                    {/* Dark gradient vignette */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent group-hover:from-black/95 transition-all duration-300 flex flex-col justify-end p-3.5 text-center pointer-events-none" />

                    {/* Category Details Tag */}
                    <div className="absolute inset-x-0 bottom-0 p-3 sm:p-3.5 flex flex-col items-center text-center z-10 transition-transform duration-300 group-hover:translate-y-[-2px]">
                      <span className="text-xs sm:text-sm font-bold text-white leading-tight drop-shadow-md line-clamp-1">
                        {cat.name}
                      </span>
                      <span className="text-[10px] text-[#F2BC6F] font-medium mt-0.5 line-clamp-1 drop-shadow-xs">
                        {cat.subtitle}
                      </span>
                    </div>
                  </Link>
                </div>
              ))}
            </div>

            {/* Desktop-Only Center Stationary Narrative (Inside the Arch Dome) */}
            <div className={`hidden md:flex absolute ${
              wheelCards.length === 0 ? 'top-[44%] -translate-y-1/2' : 'top-[370px] sm:top-[385px]'
            } left-1/2 -translate-x-1/2 w-full max-w-xl text-center px-4 z-30 pointer-events-auto flex-col items-center transition-all duration-300`}>
              
              {/* Badge Pill */}
              <div className="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-[#EFE8DC] border border-[#D9CCB9] text-neutral-800 text-[11px] font-semibold tracking-wider uppercase mb-3 shadow-2xs">
                <Compass className="w-3.5 h-3.5 text-[#6F0A06]" />
                <span>About Elavarasi</span>
              </div>

              {/* Bold Headline */}
              <h2 className="font-display font-semibold text-2xl sm:text-3xl md:text-4xl text-[#0F382A] tracking-tight leading-[1.18]">
                Helping every craving find <br className="hidden sm:inline" />
                calm, crunch, and confidence
              </h2>

              {/* Narrative Subtitle */}
              <p className="text-xs sm:text-[13px] text-neutral-600 font-normal leading-relaxed max-w-md mx-auto mt-2">
                We believe meaningful snacks start with honest heritage. Explore authentic South Indian savouries and artisanal festive keepsake hampers.
              </p>

              {/* Category Filter Switcher Tabs - moved directly above the Explore button */}
              <div className="inline-flex items-center p-1 rounded-full bg-[#EFE8DC]/95 border border-[#D9CCB9] mt-3.5 mb-1.5 shadow-2xs">
                <button
                  type="button"
                  onClick={() => setFilter('all')}
                  className={`px-3.5 py-1 text-[11px] font-semibold rounded-full transition-all cursor-pointer ${
                    filter === 'all'
                      ? 'bg-[#3D1B15] text-white shadow-xs'
                      : 'text-neutral-700 hover:text-neutral-900'
                  }`}
                >
                  All Collections
                </button>
                <button
                  type="button"
                  onClick={() => setFilter('products')}
                  className={`inline-flex items-center space-x-1.5 px-3.5 py-1 text-[11px] font-semibold rounded-full transition-all cursor-pointer ${
                    filter === 'products'
                      ? 'bg-[#0F382A] text-white shadow-xs'
                      : 'text-neutral-700 hover:text-neutral-900'
                  }`}
                >
                  <Utensils className="w-3 h-3 text-[#F2BC6F]" />
                  <span>Snacks & Savouries</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFilter('hampers')}
                  className={`inline-flex items-center space-x-1.5 px-3.5 py-1 text-[11px] font-semibold rounded-full transition-all cursor-pointer ${
                    filter === 'hampers'
                      ? 'bg-[#6F0A06] text-white shadow-xs'
                      : 'text-neutral-700 hover:text-neutral-900'
                  }`}
                >
                  <Gift className="w-3 h-3 text-amber-200" />
                  <span>Gift Hampers</span>
                </button>
              </div>

              {wheelCards.length === 0 ? (
                <div className="mt-4 p-5 sm:p-6 rounded-2xl bg-[#FAF6ED] border border-[#D9CCB9] max-w-md mx-auto text-center space-y-3 shadow-xs">
                  <div className="w-10 h-10 rounded-full bg-amber-100/80 text-amber-900 flex items-center justify-center mx-auto">
                    {filter === 'hampers' ? (
                      <Gift className="w-5 h-5 text-[#6F0A06]" />
                    ) : filter === 'products' ? (
                      <Utensils className="w-5 h-5 text-[#0F382A]" />
                    ) : (
                      <Compass className="w-5 h-5 text-[#3D1B15]" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold text-neutral-900">
                      {filter === 'hampers'
                        ? 'No Hampers Currently Listed'
                        : filter === 'products'
                        ? 'No Snack Categories Found'
                        : 'No Collections Found'}
                    </h3>
                    <p className="text-xs text-neutral-600 mt-1 leading-relaxed max-w-sm mx-auto">
                      {filter === 'hampers'
                        ? 'Our festive hampers are updated periodically. You can also craft a bespoke gift box with your chosen treats!'
                        : filter === 'products'
                        ? 'Our kitchen is handcrafting fresh traditional batches. Explore all available snacks or craft a custom box.'
                        : 'Our artisan catalog is being updated with fresh batches. Explore our full shop or craft a custom box.'}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-2.5 pt-1">
                    <Link
                      href="/custom-package"
                      className="px-4 py-2 rounded-full bg-[#0F382A] text-white text-xs font-semibold hover:bg-[#154d3a] transition-all shadow-xs cursor-pointer"
                    >
                      Build Custom Box
                    </Link>
                    <Link
                      href="/shop"
                      className="px-4 py-2 rounded-full bg-white border border-[#D9CCB9] text-neutral-800 text-xs font-semibold hover:bg-[#FAF6ED] transition-all cursor-pointer"
                    >
                      Browse All Snacks
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {/* Interactive Controls Row */}
                  <div className="mt-2.5 flex items-center justify-center space-x-3">
                    <button
                      type="button"
                      onClick={() => handleRotateBy(-stepDegree)}
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-700 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer hover:scale-105 active:scale-95"
                      title="Rotate Left"
                    >
                      <ChevronLeft className="w-4 h-4 stroke-[2.5]" />
                    </button>

                    <Link
                      href={activeCard ? activeCard.link : '/shop'}
                      onClick={() => activeCard && onSelectCategory?.(activeCard.id)}
                      className="inline-flex items-center pl-6 pr-2.5 py-2.5 rounded-full bg-[#EFE7D8] hover:bg-[#E6DBCA] border border-[#D9CCB9] text-neutral-900 text-xs sm:text-sm font-semibold transition-all shadow-xs group"
                    >
                      <span>Explore {activeCard ? activeCard.name : 'Catalogue'}</span>
                      <span className="ml-3.5 w-7 h-7 rounded-full bg-[#3D1B15] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform shadow-2xs">
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </Link>

                    <button
                      type="button"
                      onClick={() => handleRotateBy(stepDegree)}
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-700 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer hover:scale-105 active:scale-95"
                      title="Rotate Right"
                    >
                      <ChevronRight className="w-4 h-4 stroke-[2.5]" />
                    </button>

                    <button
                      type="button"
                      onClick={togglePlay}
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-500 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer hover:scale-105"
                      title={isPlaying ? 'Pause auto-rotation' : 'Resume auto-rotation'}
                    >
                      {isPlaying ? (
                        <Pause className="w-3.5 h-3.5 fill-current" />
                      ) : (
                        <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                      )}
                    </button>
                  </div>

                  <p className="text-[10px] text-neutral-400 font-medium tracking-wide mt-2">
                    Tip: Drag or use arrows to spin the wheel • Currently viewing{' '}
                    <span className="font-semibold text-neutral-700">{activeCard?.name || 'Category'}</span>
                    {' '}&bull;{' '}
                    <span className={activeCard?.typeKey === 'product' ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>
                      {activeCard?.type}
                    </span>
                  </p>
                </>
              )}

            </div>

          </div>
        </div>

        {/* 2. Mobile-Only Narrative & Interactive Controls */}
        <div className="block md:hidden px-4 sm:px-6 pt-4 pb-2 text-left space-y-3.5 max-w-lg mx-auto">
          
          {/* Badge Pill */}
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-[#EFE8DC] border border-[#D9CCB9] text-neutral-800 text-[11px] font-semibold tracking-wider uppercase shadow-2xs">
            <Compass className="w-3.5 h-3.5 text-[#6F0A06]" />
            <span>About Elavarasi</span>
          </div>

          {/* Bold Headline */}
          <h2 className="font-display font-semibold text-2xl sm:text-3xl text-[#0F382A] tracking-tight leading-[1.18]">
            Helping every craving find <br />
            calm, crunch, and confidence
          </h2>

          {/* Narrative Subtitle */}
          <p className="text-xs sm:text-[13px] text-neutral-600 font-normal leading-relaxed">
            We believe meaningful snacks start with honest heritage. Explore authentic South Indian savouries and artisanal festive keepsake hampers.
          </p>

          {/* Category Filter Switcher Tabs - moved directly above Explore button */}
          <div className="flex items-center flex-wrap gap-1.5 p-1 rounded-2xl bg-[#EFE8DC]/95 border border-[#D9CCB9] shadow-2xs w-fit pt-1">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-3 py-1 text-[11px] font-semibold rounded-full transition-all ${
                filter === 'all'
                  ? 'bg-[#3D1B15] text-white shadow-xs'
                  : 'text-neutral-700'
              }`}
            >
              All Collections
            </button>
            <button
              type="button"
              onClick={() => setFilter('products')}
              className={`inline-flex items-center space-x-1 px-3 py-1 text-[11px] font-semibold rounded-full transition-all ${
                filter === 'products'
                  ? 'bg-[#0F382A] text-white shadow-xs'
                  : 'text-neutral-700'
              }`}
            >
              <Utensils className="w-3 h-3 text-[#F2BC6F]" />
              <span>Snacks</span>
            </button>
            <button
              type="button"
              onClick={() => setFilter('hampers')}
              className={`inline-flex items-center space-x-1 px-3 py-1 text-[11px] font-semibold rounded-full transition-all ${
                filter === 'hampers'
                  ? 'bg-[#6F0A06] text-white shadow-xs'
                  : 'text-neutral-700'
              }`}
            >
              <Gift className="w-3 h-3 text-amber-200" />
              <span>Hampers</span>
            </button>
          </div>

          {wheelCards.length === 0 ? (
            <div className="pt-2 p-5 rounded-2xl bg-[#FAF6ED] border border-[#D9CCB9] text-center space-y-2.5 shadow-xs">
              <div className="w-9 h-9 rounded-full bg-amber-100/80 flex items-center justify-center mx-auto">
                {filter === 'hampers' ? (
                  <Gift className="w-4 h-4 text-[#6F0A06]" />
                ) : filter === 'products' ? (
                  <Utensils className="w-4 h-4 text-[#0F382A]" />
                ) : (
                  <Compass className="w-4 h-4 text-[#3D1B15]" />
                )}
              </div>
              <h3 className="text-xs sm:text-sm font-bold text-neutral-900">
                {filter === 'hampers'
                  ? 'No Hampers Currently Listed'
                  : filter === 'products'
                  ? 'No Snack Categories Found'
                  : 'No Collections Found'}
              </h3>
              <p className="text-[11px] text-neutral-600 leading-relaxed">
                {filter === 'hampers'
                  ? 'Craft a personalized keepsake gift box with your choice of traditional snacks.'
                  : filter === 'products'
                  ? 'Explore all fresh snacks in our store or build your own custom hamper package.'
                  : 'Check back soon for new seasonal batches or craft your own package.'}
              </p>
              <div className="flex items-center justify-center gap-2 pt-1">
                <Link
                  href="/custom-package"
                  className="px-3.5 py-1.5 rounded-full bg-[#0F382A] text-white text-[11px] font-semibold"
                >
                  Build Custom Box
                </Link>
                <Link
                  href="/shop"
                  className="px-3.5 py-1.5 rounded-full bg-white border border-[#D9CCB9] text-neutral-800 text-[11px] font-semibold"
                >
                  Browse Shop
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Interactive Controls Row at the Left Corner / Bottom */}
              <div className="pt-2 flex items-center justify-start flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => handleRotateBy(-stepDegree)}
                  className="w-10 h-10 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-700 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer active:scale-95 shrink-0"
                  title="Rotate Left"
                  aria-label="Previous category"
                >
                  <ChevronLeft className="w-4 h-4 stroke-[2.5]" />
                </button>

                <Link
                  href={activeCard ? activeCard.link : '/shop'}
                  onClick={() => activeCard && onSelectCategory?.(activeCard.id)}
                  className="inline-flex items-center pl-5 pr-2 py-2 rounded-full bg-[#EFE7D8] hover:bg-[#E6DBCA] border border-[#D9CCB9] text-neutral-900 text-xs font-semibold shadow-xs group shrink-0"
                >
                  <span>Explore {activeCard ? activeCard.name : 'Catalogue'}</span>
                  <span className="ml-3 w-6 h-6 rounded-full bg-[#3D1B15] text-white flex items-center justify-center text-xs group-hover:translate-x-0.5 transition-transform shadow-2xs">
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>

                <button
                  type="button"
                  onClick={() => handleRotateBy(stepDegree)}
                  className="w-10 h-10 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-700 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer active:scale-95 shrink-0"
                  title="Rotate Right"
                  aria-label="Next category"
                >
                  <ChevronRight className="w-4 h-4 stroke-[2.5]" />
                </button>

                <button
                  type="button"
                  onClick={togglePlay}
                  className="w-10 h-10 rounded-full bg-white hover:bg-[#FAF6ED] border border-[#D9CCB9] text-neutral-500 hover:text-[#6F0A06] flex items-center justify-center shadow-xs transition-all cursor-pointer shrink-0"
                  title={isPlaying ? 'Pause auto-rotation' : 'Resume auto-rotation'}
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? (
                    <Pause className="w-3.5 h-3.5 fill-current" />
                  ) : (
                    <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                  )}
                </button>
              </div>

              <p className="text-[10px] text-neutral-400 font-medium tracking-wide pt-1">
                Tip: Drag or use arrows to spin • Currently viewing{' '}
                <span className="font-semibold text-neutral-700">{activeCard?.name || 'Category'}</span>
                {' '}&bull;{' '}
                <span className={activeCard?.typeKey === 'product' ? 'text-emerald-700 font-semibold' : 'text-amber-700 font-semibold'}>
                  {activeCard?.type}
                </span>
              </p>
            </>
          )}
        </div>

        {/* 3. Bottom 4-Column Statistics Bar */}
        <div className="mt-6 pt-10 border-t border-neutral-200/80 max-w-5xl lg:max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          {STATS_DATA.map((stat, idx) => (
            <div
              key={idx}
              className={`space-y-1 ${idx > 0 ? 'md:border-l md:border-neutral-200/80' : ''}`}
            >
              <div className="font-display font-semibold text-2xl sm:text-3xl lg:text-4xl text-neutral-900">
                {stat.value}
              </div>
              <div className="text-xs text-neutral-500 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
